import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import AppTableWithPageMeta from "components/AppTableWithPageMeta";
import DeleteConfirmationModal from "components/genericForms/DeleteConfirmationModal";
import currencySymbolMap from "currency-symbol-map";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";
import { MdDelete, MdEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useDeleteAdsetMutation,
  useDuplicateAdsetMutation,
  useGetAdsetInsightsQuery,
  useGetAdsetsListQuery,
  useUpdateAdsetStatusMutation
} from "store/campaigns/adsetFormApi";
import {
  selectAdsetsForInsights,
  selectAfter,
  selectBefore,
  selectCurrentPage,
  selectSelectedAdsetId,
  selectSelectedCampaignId,
  setAdsetsForInsights,
  setAfter,
  setBefore,
  setCampaignListLoading,
  setCurrentPage,
  setSelectedAdset,
  setSelectedAdsetId,
} from "store/campaigns/campaignsSlice";
import CreateAdsetForm from "./Forms/AdsetForms/CreationAdsetForms/CreateAdsetForm";
import AdSetUpdateForm from "./Forms/AdsetForms/UpdationAdsetForms/AdsetUpdateForm";

function AdSetsTable({
  adsAccountData,
  startDate,
  endDate,
  datePreset,
  setSelectedTab,
  onRefresh,
  onRefreshComplete,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const selectedCampaignId = useSelector(selectSelectedCampaignId);
  const isCampaignSelected = Boolean(selectedCampaignId);
  const selectedAdsetId = useSelector(selectSelectedAdsetId);
  const AdsetsForInsights = useSelector(selectAdsetsForInsights);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const isMounted = useRef(false);
  const urlInitDoneRef = useRef(false);
  const prevAccountIdRef = useRef(adsAccountData?.account_id);
  const after = useSelector(selectAfter);
  const before = useSelector(selectBefore);
  const currentPage = useSelector(selectCurrentPage);
  const isSearchDisabled = Boolean(selectedAdsetId);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDuplicateModalOpen,
    onOpen: onDuplicateModalOpen,
    onClose: onDuplicateModalClose,
  } = useDisclosure();

  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedAdset, setselectedAdset] = useState(null);
  const [switchStates, setSwitchStates] = useState({});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [adsetsListWithInsights, setAdsetsListWithInsights] = useState([]);

  // Loader flags
  const [isLoadingAdsetsWithInsights, setIsLoadingAdsetsWithInsights] = useState(true);

  const [copyCount, setCopyCount] = useState(1);
  const currencyCode = adsAccountData?.currency;
  const currencySymbol = currencySymbolMap(adsAccountData?.currency);

  const {
    data: adsetsList,
    isFetching: isAdsetListFetching,
    isLoading: isAdsetListLoading,
    error: adsetsListError,
    isUninitialized: isAdsetListUninitialized,
    refetch: refetchAdsetList,
  } = useGetAdsetsListQuery(
    {
      pageSize: "20",
      ...(searchValue ? {} : { after, before }),
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      campaign_ids: selectedCampaignId
        ? [selectedCampaignId?.campaign_id ? selectedCampaignId?.campaign_id : selectedCampaignId]
        : [],
      adset_id: selectedAdsetId?.adset_id ? selectedAdsetId?.adset_id : selectedAdsetId,
    },
    { skip: !adsAccountData?.account_id }
  );

  const {
    data: adsetsInsights,
    isUninitialized: isAdsetInsightsUninitialized,
    isFetching: isAdsetInsightListFetching,
    refetch: refetchAdsetInsights,
    isLoading: isAdsetInsightsLoading,
    error: AdsetsInsightsError,
  } = useGetAdsetInsightsQuery(
    {
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      ...(datePreset ? {} : { start_date: startDate, end_date: endDate }),
      date_preset: datePreset,
      adsets: AdsetsForInsights?.length > 0 ? AdsetsForInsights : [],
    },
    { skip: !adsAccountData?.account_id || AdsetsForInsights?.length === 0 }
  );

  const [updateAdsetStatus] = useUpdateAdsetStatusMutation({});
  const [deleteAdset] = useDeleteAdsetMutation();
  const [duplicateAdset, { isLoading: isAdsetDuplicating }] = useDuplicateAdsetMutation();

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  useEffect(() => {
    const prevId = prevAccountIdRef.current;
    const currId = adsAccountData?.account_id;

    if (prevId && currId && prevId !== currId) {
          setIsLoadingAdsetsWithInsights(true);
          setselectedAdset(null);
          setSwitchStates({});
          setSearchValue("");
          setAdsetsListWithInsights([]);
    
          dispatch(setSelectedAdsetId(null));
          dispatch(setSelectedAdset(null));
          dispatch(setAdsetsForInsights([]));
          dispatch(setAfter(""));
          dispatch(setBefore(""));
          dispatch(setCurrentPage(0));
    }
    prevAccountIdRef.current = currId;
  }, [adsAccountData?.account_id]);

  // External refresh trigger
  useEffect(() => {
    if (onRefresh) {
      setIsLoadingAdsetsWithInsights(true);
      refetchAdsetList();
      refetchAdsetInsights();
      onRefreshComplete();
    }
  }, [onRefresh, refetchAdsetList, onRefreshComplete]);

  // Build switch states & prep insight query list
  useEffect(() => {
    if (adsetsList?.data) {
      const initialSwitchStates = {};
      adsetsList.data.forEach((adset) => {
        initialSwitchStates[adset.adset_id] = adset.status;
      });
      setSwitchStates(initialSwitchStates);

      const formattedAdsets = adsetsList.data.reduce((acc, adset) => {
        const promotedObject = adset?.promoted_object;
        if (adset?.adset_id) {
          acc.push({
            id: adset?.adset_id,
            promoted_object: promotedObject,
            destination_type: adset?.destination_type,
            optimization_goal: adset?.optimization_goal,
          });
        } else {
          acc.push({
            id: adset?.adset_id,
            destination_type: adset?.destination_type,
            optimization_goal: adset?.optimization_goal,
          });
        }
        return acc;
      }, []);
      dispatch(setAdsetsForInsights(formattedAdsets));
    }
  }, [adsetsList]);

  // Stitch list + insights and then drop the loader
  useEffect(() => {
    if (adsetsList?.data || adsetsInsights) {
      const adsetsWithInsights = (adsetsList?.data || []).map((adset) => {
        const insightData = adsetsInsights?.find(
          (insight) => insight?.adset_id === adset?.adset_id
        );
        return {
          ...adset,
          insight: insightData?.insights || {},
        };
      });

      setAdsetsListWithInsights(adsetsWithInsights);

      // tiny debounce to avoid flicker
      const t = setTimeout(() => {
        setIsLoadingAdsetsWithInsights(false);
      }, 100);
      return () => clearTimeout(t);
    }
  }, [adsetsList, adsetsInsights]);

  const handleSwitchChange = (adsetId, currentState) => {
    const newStatus = currentState === "ACTIVE" ? "PAUSED" : "ACTIVE";
    setSwitchStates((prev) => ({ ...prev, [adsetId]: newStatus }));

    updateAdsetStatus({
      adsetId,
      payload: {
        status: newStatus,
        adsAccount: {
          id: adsAccountData?.id,
          adsAccountId: adsAccountData?.account_id,
          adsAccountName: adsAccountData?.name,
          businessName: adsAccountData?.business?.name,
          businessId: adsAccountData?.business?.id,
        },
      },
    })
      .unwrap()
      .then(() => {
        if (isMounted.current) {
          refetchAdsetList();
          toast({
            position: "top-right",
            title: `Adset ${newStatus === "ACTIVE" ? "activated" : "paused"}`,
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        if (isMounted.current) {
          toast({
            position: "top-right",
            title: `Error updating Adset status`,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
        console.error("Error updating Adset status:", error);
        setSwitchStates((prev) => ({ ...prev, [adsetId]: currentState }));
      });
  };

  const handleDuplicateAdset = () => {
    duplicateAdset({ adsetId: selectedAdset?.adset_id, payload: { copy_count: copyCount } })
      .unwrap()
      .then(() => {
        refetchAdsetList();
        toast({
          position: "top-right",
          title: "Adset duplicated sucessfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: "top-right",
          title: "Error duplicating adset",
          status: "error",
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => {
        setselectedAdset(null);
        onDuplicateModalClose();
      });
  };

  const handlePageChange = ({ after, before, currentPage }) => {
    if (after) {
      dispatch(setAfter(after));
      dispatch(setBefore(""));
    } else if (before) {
      dispatch(setBefore(before));
      dispatch(setAfter(""));
    }
    dispatch(setCurrentPage(currentPage));
  };

  // 1) On mount, read URL once and seed Redux
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stored = params.get('page_id');
    const parsed = stored !== null ? Number(stored) : NaN;

    if (Number.isInteger(parsed) && parsed >= 0) {
      dispatch(setCurrentPage(parsed));
    } else {
      // write page_id=0 into URL so both stay in sync from the start
      const next = new URLSearchParams(location.search);
      next.set('page_id', '0');
      navigate({ search: next.toString() }, { replace: true });
    }
    urlInitDoneRef.current = true;
  }, []);

  // 2) Whenever currentPage changes, push it to the URL (no readback loop)
  useEffect(() => {
    if (!urlInitDoneRef.current) return;

    const params = new URLSearchParams(location.search);
    const existing = params.get('page_id');
    if (existing === String(currentPage)) return; // already in sync

    params.set('page_id', String(currentPage));
    navigate({ search: params.toString() }, { replace: true });
    // only depend on currentPage & location.search so we re-evaluate when URL actually changes
  }, [currentPage, location.search]);

  const handleDeleteAdset = (adsetData) => {
    deleteAdset({ adsetId: adsetData?.adset_id, adsAccountId: adsAccountData?.account_id })
      .unwrap()
      .then(() => {
        refetchAdsetList();
        dispatch(setCampaignListLoading(true));
        toast({
          position: "top-right",
          title: "Ad set deleted",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: "top-right",
          title: "Error deleting ad set",
          status: "error",
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Off/On",
        accessor: "",
        Filter: "",
        filter: "",
        width: 120,
        Cell: ({ row: { original } }) => {
          const isActive = switchStates[original.adset_id] ?? original.status === "ACTIVE";
          const tooltipText = isActive ? "Ad set is ON" : "Ad set is OFF";

          return (
            <Flex gap={"10px"} alignItems={"center"} justifyContent={"center"}>
              <Tooltip label={tooltipText} fontSize="xs">
                <FormControl width={"fit-content"}>
                  <Switch
                    colorScheme="brand"
                    size={"sm"}
                    isChecked={original.status === "ACTIVE"}
                    onChange={() => handleSwitchChange(original.adset_id, original.status)}
                    isDisabled={userInfo?.role !== "client"}
                  />
                </FormControl>
              </Tooltip>
            </Flex>
          );
        },
      },
      {
        Header: "Name",
        accessor: "",
        Filter: "",
        filter: "",
        width: 300,
        Cell: ({ row: { original } }) => {
          const isSelected = selectedAdsetId?.adset_id
            ? selectedAdsetId?.adset_id
            : selectedAdsetId === original?.adset_id;

          return (
            <Box>
              <Box>
                <Flex gap={"10px"} alignItems={"center"}>
                  <Link
                    to={"#"}
                    onClick={() => {
                      setSelectedTab(2);
                      dispatch(setSelectedAdsetId(original));
                      dispatch(setSelectedAdset(original));
                    }}
                  >
                    <Text
                      _hover={{ textDecoration: "underline", cursor: "pointer" }}
                      fontWeight={isSelected ? "bold" : "normal"}
                      color={isSelected ? "brand.500" : "inherit"}
                      fontSize={"14px"}
                    >
                      {original.name}
                    </Text>
                  </Link>
                </Flex>
              </Box>
              <Box display={"flex"} dir="row" gap={2}>
                <Flex
                  alignItems={"center"}
                  gap={1}
                  onClick={() => {
                    if (userInfo?.role === "client") {
                      setselectedAdset(original);
                      onOpen();
                    }
                  }}
                >
                  <Icon
                    as={MdEdit}
                    cursor={userInfo?.role === "client" ? "pointer" : "not-allowed"}
                    color={userInfo?.role === "client" ? "inherit" : "gray.300"}
                    fontSize={"12px"}
                  />
                  <Link to={"#"}>
                    <Text
                      _hover={{ textDecoration: "underline", cursor: "pointer" }}
                      fontWeight={"normal"}
                      color={"inherit"}
                      fontSize={"11px"}
                    >
                      Edit
                    </Text>
                  </Link>
                </Flex>
                <Flex
                  alignItems={"center"}
                  gap={1}
                  onClick={() => {
                    if (userInfo?.role === "client") {
                      setselectedAdset(original);
                      onDuplicateModalOpen();
                    }
                  }}
                >
                  <Icon
                    as={HiOutlineDocumentDuplicate}
                    cursor={userInfo?.role === "client" ? "pointer" : "not-allowed"}
                    color={userInfo?.role === "client" ? "inherit" : "gray.300"}
                    fontSize={"12px"}
                  />
                  <Link to={"#"}>
                    <Text
                      _hover={{ textDecoration: "underline", cursor: "pointer" }}
                      fontWeight={"normal"}
                      color={"inherit"}
                      fontSize={"11px"}
                    >
                      Duplicate
                    </Text>
                  </Link>
                </Flex>
              </Box>
            </Box>
          );
        },
      },
      {
        Header: "Delivery",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const getStatusMessage = (status) => {
            switch (status) {
              case "WITH_ISSUES": return "Delivery error";
              case "CAMPAIGN_PAUSED": return "Campaign Off";
              case "ADSET_PAUSED": return "Adset Off";
              case "PAUSED": return "Off";
              default: return status || "Unknown";
            }
          };

          const errorMessage = original?.issues_info?.[0]?.error_message || "";

          const getCircleStyles = (status) => {
            switch (status) {
              case "WITH_ISSUES": return { bg: "rgb(161,11,24)", border: "none" };
              case "CAMPAIGN_PAUSED":
              case "ADSET_PAUSED": return { bg: "rgb(216,221,225)", border: "none" };
              case "ACTIVE":
              case "IN_PROCESS":
              case "PENDING_REVIEW": return { bg: "rgb(255,255,255)", border: "2px solid green" };
              default: return { bg: "rgb(255,255,255)", border: "none" };
            }
          };

          const circleStyles = getCircleStyles(original?.effective_status);

          const formatTooltipMessage = (message) =>
            message.split("\n").map((part, index) => (
              <span key={index}>
                {part}
                <br />
              </span>
            ));

          const getTooltipMessage = (status) => {
            switch (status) {
              case "WITH_ISSUES": return errorMessage;
              case "PAUSED": return "This ad set is turned off.";
              case "ACTIVE": return "This campaign is on and running normally.";
              case "ADSET_PAUSED": return "This ad is on, but its ad set is turned off.";
              case "CAMPAIGN_PAUSED": return "This ad set is on, but its campaign is turned off.";
              case "IN_PROCESS": return "Your ad is processing.\nMeta is processing your recent updates to your ad. Some updates may take several hours.";
              default: return "";
            }
          };

          const tooltipMessage = getTooltipMessage(original?.effective_status);

          return (
            <Flex>
              {original?.effective_status === "WITH_ISSUES" ? (
                <Flex alignItems={"center"}>
                  <Box
                    bg={circleStyles.bg}
                    border={circleStyles.border}
                    borderRadius="50%"
                    boxSize="0.8rem"
                    display="inline-block"
                    mr={2}
                  />
                  <Tooltip
                    label={formatTooltipMessage(tooltipMessage)}
                    aria-label="Error message"
                    hasArrow
                    p={2}
                    fontSize={"12px"}
                  >
                    <Text fontSize={"14px"}>{getStatusMessage(original?.effective_status)}</Text>
                  </Tooltip>
                </Flex>
              ) : (
                <Flex alignItems={"center"}>
                  {original?.effective_status === "PAUSED" ||
                  original?.effective_status === "ARCHIVED" ? null : (
                    <Box
                      bg={circleStyles.bg}
                      border={circleStyles.border}
                      borderRadius="50%"
                      boxSize="0.8rem"
                      display="inline-block"
                      mr={2}
                    />
                  )}
                  {original?.effective_status !== "PENDING_REVIEW" && (
                    <Tooltip
                      label={formatTooltipMessage(tooltipMessage)}
                      aria-label="Error message"
                      hasArrow
                      p={2}
                      fontSize={"12px"}
                    >
                      <Text fontSize={"14px"}>{getStatusMessage(original?.effective_status)}</Text>
                    </Tooltip>
                  )}
                  {original?.effective_status === "PENDING_REVIEW" && (
                    <Text fontSize={"14px"}>{getStatusMessage(original?.effective_status)}</Text>
                  )}
                </Flex>
              )}
            </Flex>
          );
        },
      },
      {
        Header: "Budget",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const { daily_budget, lifetime_budget } = original;

          const displayLifetimeBudget =
            Number(lifetime_budget) > 0 ? (Number(lifetime_budget) / 100).toFixed(2) : null;
          const displayDailyBudget =
            Number(daily_budget) > 0 ? (Number(daily_budget) / 100).toFixed(2) : null;

          const isLifetimeBudget = displayLifetimeBudget !== null;
          const displayBudget = isLifetimeBudget
            ? `${currencySymbol ?? ""}${displayLifetimeBudget}`
            : displayDailyBudget !== null
            ? `${currencySymbol ?? ""}${displayDailyBudget}`
            : "No Budget";
          const budgetType = isLifetimeBudget ? "Lifetime" : displayDailyBudget !== null ? "Daily" : "";

          return (
            <Flex direction={"column"}>
              <Text fontSize={"14px"}>{displayBudget}</Text>
              {budgetType && (
                <Text fontSize={"10px"} color={"gray.500"}>
                  {budgetType}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        Header: "Spend",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex>
            <Text fontSize={"14px"}>
              {original.insight?.spend
                ? `${currencySymbol}${Number(original.insight.spend).toFixed(2)}`
                : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "Cost per result",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original?.insight?.cpr ? `${currencySymbol}${Number(original.insight.cpr).toFixed(2)}` : '-'}
            </Text>
          </Flex>
        )
      },
      {
        Header: "Results",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const { insight, destination_type, optimization_goal } = original;

          const eventNameMap = {
            destination_type: {
              ON_AD: "Meta Leads",
              App: "In-app subscribes",
            },
            optimization_goal: {
              APP_INSTALLS: "Mobile App Installation",
              LEAD_GENERATION: "Leads",
              THRUPLAY: "Thruplay",
              LINK_CLICKS: "Link Clicks",
              PAGE_LIKES: "Follows or Likes",
              QUALITY_CALL: "20-second Call",
              REACH: 'Reach',
              IMPRESSIONS: 'Reach'
            },
          };

          let derivedEventName = null;
          if (eventNameMap.destination_type[destination_type]) {
            derivedEventName = eventNameMap.destination_type[destination_type];
          } else if (eventNameMap.optimization_goal[optimization_goal]) {
            derivedEventName = eventNameMap.optimization_goal[optimization_goal];
          }

          const originalEventName = insight?.eventName
            ? insight.eventName.charAt(0).toUpperCase() + insight.eventName.slice(1)
            : null;

          return (
            <Flex direction="column" mb={-4}>
              <Text fontSize="14px">
                {insight?.results ? insight.results : "-"}
              </Text>
              {(derivedEventName || originalEventName) && (
                <Text fontSize="10px" color="gray.500">
                  {derivedEventName || originalEventName}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        Header: "CPC",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.cpc
                ? `${currencySymbol}${Number(original.insight?.cpc).toFixed(2)}`
                : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "CTR",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.ctr ? `${Number(original.insight?.ctr).toFixed(2)}%` : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "CPM",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.cpm ? Number(original.insight?.cpm).toFixed(2) : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "Clicks",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.clicks ? original.insight?.clicks : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "Purchase Value",
        accessor: "",
        Filter: "",
        filter: "",
        width: 180,
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.purchaseValue && original.insight.purchaseValue !== "-"
                ? `${currencySymbol}${parseFloat(original.insight.purchaseValue).toFixed(2)}`
                : original.insight?.purchaseValue || "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "Roas",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const roas = original.insight?.roas;
          const formattedROAS =
            roas && !isNaN(parseFloat(roas)) ? parseFloat(roas).toFixed(2) : "-";
          return (
            <Flex>
              <Text fontSize={"14px"}>{formattedROAS}</Text>
            </Flex>
          );
        },
      },
      {
        Header: "hook Rate",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.hookRate
                ? `${Number(original.insight?.hookRate).toFixed(2)}%`
                : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "hold Rate",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"}>
            <Text fontSize={"14px"}>
              {original.insight?.holdRate
                ? `${Number(original.insight?.holdRate).toFixed(2)}%`
                : "-"}
            </Text>
          </Flex>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => <Text fontSize={"14px"}>{original.status}</Text>,
      },
      {
        Header: "Created Date",
        accessor: "createdDate",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Text fontSize={"14px"}>
            {moment(original.created_time).format("DD-MM-YYYY hh:mm A")}
          </Text>
        ),
      },
      {
        Header: "Updated Date",
        accessor: "updatedDate",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Text fontSize={"14px"}>
            {moment(original.updated_time).format("DD-MM-YYYY hh:mm A")}
          </Text>
        ),
      },
      {
        Header: "Actions",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={"10px"} alignItems={"center"}>
            <Tooltip label="Delete" fontSize="xs">
              <Flex>
                <Icon
                  _hover={{ color: "gray.400" }}
                  as={MdDelete}
                  cursor={userInfo?.role === "client" ? "pointer" : "not-allowed"}
                  color={userInfo?.role === "client" ? "red.500" : "gray.300"}
                  onClick={() => {
                    if (userInfo?.role === "client") {
                      setselectedAdset(original);
                      setIsConfirmationModalOpen(true);
                    }
                  }}
                />
              </Flex>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [selectedAdsetId, adsAccountData, switchStates, userInfo?.role]
  );

  const confirmDelete = () => {
    if (selectedAdset) {
      handleDeleteAdset(selectedAdset);
      setIsConfirmationModalOpen(false);
      setselectedAdset(null);
    }
  };

  const handleOnSuccess = () => {
    if (!isAdsetListUninitialized) {
      refetchAdsetList();
    }
    if (!isAdsetInsightsUninitialized) {
      refetchAdsetInsights();
    }
    dispatch(setCampaignListLoading(true));
  };

  const handleSaveSuccess = () => {
    refetchAdsetList();
  };

  return (
    <>
      {selectedCampaignId?.smart_promotion_type === "AUTOMATED_SHOPPING_ADS" &&
      adsetsList?.data?.length > 0 ? null : (
        <Flex mb={2} alignItems="center">
          <Tooltip
            p={2}
            label="To create an Ad set, please select a Campaign from the Campaigns tab."
            isDisabled={isCampaignSelected}
            placement="right"
            borderRadius={"sm"}
            fontSize={"12px"}
          >
            <Button
              leftIcon={<AddIcon boxSize={3} />}
              colorScheme={isCampaignSelected ? "brand" : "gray"}
              size="sm"
              onClick={() => {
                if (isCampaignSelected) {
                  setIsCreateFormVisible(true);
                }
              }}
              _hover={{ cursor: isCampaignSelected ? "pointer" : "not-allowed" }}
              _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            >
              Create
            </Button>
          </Tooltip>
        </Flex>
      )}

      {isCreateFormVisible && (
        <CreateAdsetForm
          adsAccountData={adsAccountData}
          isOpen={isCreateFormVisible}
          onClose={() => setIsCreateFormVisible(false)}
          onSuccess={handleOnSuccess}
        />
      )}

      <DeleteConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this Ad set? This action can't be undone."
      />

      {/* DUPLICATE ADSET MODAL */}
      <Modal
        size={"xl"}
        isCentered={true}
        isOpen={isDuplicateModalOpen}
        scrollBehavior={"inside"}
        onClose={onDuplicateModalClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={"md"}>Duplicate your ad set</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gap={1} direction={"column"}>
              <Text fontSize={"14px"} fontWeight={"bold"}>
                Number of copies
              </Text>
              <NumberInput
                value={copyCount}
                onChange={(valueString, valueNumber) => setCopyCount(valueNumber)}
                min={1}
                size="sm"
                width={"530px"}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper border={"none"} />
                  <NumberDecrementStepper border={"none"} />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button size={"sm"} mr={3} onClick={onDuplicateModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isAdsetDuplicating}
              size={"sm"}
              onClick={handleDuplicateAdset}
            >
              Duplicate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* EDIT ADSET MODAL */}
      <Modal
        size={"6xl"}
        isCentered={true}
        isOpen={isOpen}
        scrollBehavior={"inside"}
        onClose={() => {
          onClose();
          setselectedAdset(null);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Ad set</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AdSetUpdateForm
              selectedAdset={selectedAdset}
              adsAccountData={adsAccountData}
              onClose={onClose}
              onSaveSuccess={handleSaveSuccess}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {adsetsListError ? (
        <Flex justifyContent={"center"} alignItems={"center"} height={"50vh"}>
          <Text color="red.500">
            {adsetsListError?.data?.message}
          </Text>
        </Flex>
      ) : (isAdsetListLoading || isAdsetInsightsLoading || isLoadingAdsetsWithInsights || onRefresh) ? (
        <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'50vh'}>
          <Spinner />
          <Text>Loading…</Text>
        </Flex>
      ) : (
        <AppTableWithPageMeta
          key={adsAccountData?.account_id || "no-account"} // hard reset table state on account switch
          adsAccountId={adsAccountData?.account_id}
          datePreset={datePreset}
          currencyCode={currencyCode}
          columns={columns}
          data={adsetsListWithInsights || []}
          searchEnabled={!isSearchDisabled}
          isFetching={isAdsetListFetching || isAdsetInsightListFetching}
          initialState={{ pageIndex: currentPage }}
          pageMeta={adsetsList?.meta || {}}
          onPageChange={handlePageChange}
          onSearchInputChange={setSearchValue}
          searchTerm={searchValue}
        />
      )}
    </>
  );
}

export default AdSetsTable;
